import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Star, CheckCircle2, XCircle, Loader2, RefreshCw } from "lucide-react";
import { api } from "../../lib/api";

/**
 * Painel admin de moderação de avaliações.
 * Filtro por status: pending (default) | approved | rejected | all.
 */
export const ReviewsPanel = () => {
  const [filter, setFilter] = useState("pending");
  const [data, setData] = useState({ items: [], counts: { pending: 0, approved: 0, rejected: 0 } });
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = filter === "all" ? "" : `?status=${filter}`;
      const { data } = await api.get(`/admin/reviews${q}`);
      setData({
        items: Array.isArray(data?.items) ? data.items : [],
        counts: data?.counts && typeof data.counts === "object"
          ? { pending: 0, approved: 0, rejected: 0, ...data.counts }
          : { pending: 0, approved: 0, rejected: 0 },
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao carregar avaliações");
      setData({ items: [], counts: { pending: 0, approved: 0, rejected: 0 } });
    } finally { setLoading(false); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const moderate = async (r, status) => {
    setBusyId(r.id);
    try {
      await api.post(`/admin/reviews/${r.id}/moderate`, { status });
      toast.success(status === "approved" ? "Avaliação aprovada" : "Avaliação rejeitada");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro na moderação");
    } finally { setBusyId(null); }
  };

  const Btn = ({ v, label, count }) => (
    <button onClick={() => setFilter(v)} data-testid={`reviews-filter-${v}`}
      className={`text-sm px-3 py-1.5 rounded-md border inline-flex items-center gap-1.5 ${filter === v ? "bg-[#0B2B62] text-white border-[#0B2B62]" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
      {label} {count !== undefined && <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${filter === v ? "bg-white/20" : "bg-slate-100"}`}>{count}</span>}
    </button>
  );

  return (
    <div data-testid="admin-reviews-panel">
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <h3 className="font-display font-bold text-xl text-[#0B2B62]">Moderação de Avaliações</h3>
        <button onClick={load} className="text-xs px-2 py-1.5 border border-slate-200 rounded-md hover:bg-slate-50 inline-flex items-center gap-1">
          <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Atualizar
        </button>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        <Btn v="pending" label="Pendentes" count={data.counts?.pending} />
        <Btn v="approved" label="Aprovadas" count={data.counts?.approved} />
        <Btn v="rejected" label="Rejeitadas" count={data.counts?.rejected} />
        <Btn v="all" label="Todas" />
      </div>

      {loading ? (
        <p className="text-sm text-slate-500">Carregando…</p>
      ) : data.items.length === 0 ? (
        <p className="text-sm text-slate-500 py-8 text-center">Nenhuma avaliação nesse filtro.</p>
      ) : (
        <div className="space-y-3">
          {data.items.map((r) => (
            <div key={r.id} data-testid={`review-row-${r.id}`}
                 className="p-4 border border-slate-200 rounded-lg bg-white">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    {[1,2,3,4,5].map((n) => (
                      <Star key={n} className={`w-4 h-4 ${n <= r.stars ? "text-amber-400" : "text-slate-200"}`}
                            fill={n <= r.stars ? "currentColor" : "none"} />
                    ))}
                    <span className="text-xs font-bold text-slate-700 ml-1">{r.stars}/5</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase font-bold tracking-widest ${
                      r.status === "approved" ? "bg-emerald-100 text-emerald-700" :
                      r.status === "rejected" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"
                    }`}>{r.status}</span>
                    {!r.allow_public && <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 uppercase font-bold tracking-widest">privada</span>}
                  </div>
                  <div className="text-xs text-slate-500 mb-1">
                    <b>{r.family_display || r.family_name}</b> · {r.protocol} · {r.city || "—"}
                    {r.consultant_name && <> · consultor {r.consultant_name}</>}
                    {r.clinic_name && <> · clínica {r.clinic_name}</>}
                  </div>
                  {r.comment ? (
                    <p className="text-sm text-slate-700 mt-2 italic bg-slate-50 rounded-md p-2.5">"{r.comment}"</p>
                  ) : (
                    <p className="text-xs text-slate-400 mt-2 italic">(sem comentário)</p>
                  )}
                </div>
                <div className="flex gap-2 shrink-0">
                  {r.status !== "approved" && (
                    <button onClick={() => moderate(r, "approved")} disabled={busyId === r.id}
                      data-testid={`review-approve-${r.id}`}
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-700 text-white rounded-md text-xs font-semibold hover:bg-emerald-800 disabled:opacity-50">
                      {busyId === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                      Aprovar
                    </button>
                  )}
                  {r.status !== "rejected" && (
                    <button onClick={() => moderate(r, "rejected")} disabled={busyId === r.id}
                      data-testid={`review-reject-${r.id}`}
                      className="inline-flex items-center gap-1 px-3 py-1.5 border border-red-300 text-red-600 rounded-md text-xs font-semibold hover:bg-red-50 disabled:opacity-50">
                      <XCircle className="w-3 h-3" /> Rejeitar
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReviewsPanel;
